// ---------------------------------------------------------------------------
// EventBindingRow — a single event binding (e.g., tap → submitForm).
//
// Shows the event type icon, bound flow name, and edit/remove controls.
// If no binding exists, shows a flow picker to create one.
// Flow names are clickable — navigates to Flow mode and selects the flow.
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react'
import { Pencil, X, Zap, MousePointerClick, FormInput, Focus, EyeOff, Send, Move, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CogoEventRef } from '@/features/page-editor/store/types'
import { FlowPicker } from './flow-picker'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import { summarizeFlowActions } from '@/lib/cross-reference'

const EVENT_ICONS: Record<string, React.FC<{ className?: string; size?: string | number }>> = {
  tap: MousePointerClick,
  click: MousePointerClick,
  change: FormInput,
  focus: Focus,
  blur: EyeOff,
  submit: Send,
  hover: Move,
}

const EVENT_LABELS: Record<string, string> = {
  tap: 'Tap / Click',
  click: 'Click',
  change: 'Change',
  focus: 'Focus',
  blur: 'Blur',
  submit: 'Submit',
  hover: 'Hover',
}

interface EventBindingRowProps {
  eventType: string
  binding?: CogoEventRef
  availableFlows: string[]
  onBind: (ref: CogoEventRef) => void
  onUnbind: () => void
  /** Return false to reject the creation (duplicate name) — passed through
   *  to FlowPicker, which then keeps its input state and does not bind. */
  onCreateFlow?: (flowName: string) => boolean | void
}

export function EventBindingRow({
  eventType,
  binding,
  availableFlows,
  onBind,
  onUnbind,
  onCreateFlow,
}: EventBindingRowProps) {
  const [editing, setEditing] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const editorFlows = useEditorStore((s) => s.flows)
  const flowEditorFlows = useFlowEditorStore((s) => s.flows)
  // Step counts/summaries prefer the bridge-maintained currentProject.flows
  // mirror — editorStore.flows step arrays are frozen at load time (F8).
  const projectFlows = useStudioStore((s) => s.currentProject?.flows)

  const Icon = EVENT_ICONS[eventType] ?? Zap
  const label = EVENT_LABELS[eventType] ?? eventType

  const flowName = binding?.flow ?? binding?.action

  // Check if the referenced flow actually exists
  const flowExists = useMemo(() => {
    if (!flowName) return false
    // Check in editor store flows (Coogo JSON flows)
    if (flowName in editorFlows) return true
    // Check in flow editor store flows (visual diagram flows)
    if (flowEditorFlows.some((f) => f.name === flowName || f.id === flowName)) return true
    return false
  }, [flowName, editorFlows, flowEditorFlows])

  // Get flow summary for tooltip
  const flowSummary = useMemo(() => {
    if (!flowName || !flowExists) return null
    const flow = projectFlows?.[flowName] ?? editorFlows[flowName]
    if (!flow) return null
    const stepCount = flow.steps?.length ?? 0
    const summary = summarizeFlowActions(flow)
    return { stepCount, summary }
  }, [flowName, flowExists, projectFlows, editorFlows])

  // Navigate to flow editor and select the flow
  const handleFlowClick = () => {
    if (!flowName || !flowExists) return
    // Switch to flow mode
    useStudioStore.getState().setMode('flow')
    // Find and select the flow in the flow editor
    const flowMeta = flowEditorFlows.find((f) => f.name === flowName || f.id === flowName)
    if (flowMeta) {
      useFlowEditorStore.getState().selectFlow(flowMeta.id)
    }
  }

  if (!binding && !editing) {
    return (
      <button
        type="button"
        className="flex items-center gap-1.5 w-full px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted/50 rounded transition-colors"
        onClick={() => setEditing(true)}
      >
        <Icon size={12} className="shrink-0" />
        <span>{label}</span>
        <span className="flex-1" />
        <span className="text-[10px] text-muted-foreground/60">+ Bind</span>
      </button>
    )
  }

  if (editing || !flowName) {
    return (
      <div className="flex items-center gap-1.5 px-1.5 py-1">
        <Icon size={12} className="shrink-0 text-muted-foreground" />
        <span className="text-xs text-muted-foreground shrink-0">{label}</span>
        <div className="flex-1">
          <FlowPicker
            value={flowName}
            availableFlows={availableFlows}
            onSelect={(flow) => {
              onBind({ flow })
              setEditing(false)
            }}
            onCreateNew={onCreateFlow}
          />
        </div>
        {binding && (
          <button
            type="button"
            className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors"
            onClick={() => setEditing(false)}
            title="Cancel"
          >
            <X size={10} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={cn(
      'flex items-center gap-1.5 px-1.5 py-1 rounded group',
      'hover:bg-muted/30 transition-colors',
    )}>
      <Icon size={12} className="shrink-0 text-primary/70" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>

      {/* Flow name — clickable to navigate, with tooltip and missing-flow warning */}
      <div className="relative flex-1 min-w-0">
        {flowExists ? (
          <button
            type="button"
            className="text-xs font-mono truncate text-primary/80 hover:text-primary hover:underline cursor-pointer block w-full text-left"
            onClick={handleFlowClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            title={`Go to flow: ${flowName}`}
          >
            {flowName}
          </button>
        ) : (
          <span className="flex items-center gap-1 text-xs font-mono truncate text-destructive">
            <AlertTriangle size={10} className="shrink-0" />
            <span className="truncate">{flowName}</span>
            <span className="text-[10px] text-destructive/70 shrink-0">Not found</span>
          </span>
        )}

        {/* Mini tooltip showing step count and action summary */}
        {showTooltip && flowSummary && (
          <div className="absolute z-50 bottom-full left-0 mb-1 bg-popover border border-border rounded-lg shadow-lg px-2 py-1.5 text-[10px] whitespace-nowrap pointer-events-none">
            <div className="text-foreground font-medium">{flowName}</div>
            <div className="text-muted-foreground">{flowSummary.stepCount} step{flowSummary.stepCount !== 1 ? 's' : ''}</div>
            {flowSummary.summary !== 'empty' && (
              <div className="text-muted-foreground/80 font-mono">{flowSummary.summary}</div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors"
          onClick={() => setEditing(true)}
          title="Edit binding"
        >
          <Pencil size={10} />
        </button>
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-destructive rounded transition-colors"
          onClick={onUnbind}
          title="Remove binding"
        >
          <X size={10} />
        </button>
      </div>
    </div>
  )
}
